import { cacheFactory } from '@shared/helpers/function.helpers';
import {
  doPolygonsOverlapInterior,
  getPolygonOverlapArea,
  getSharedBoundaryLength,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import { isCloseTo } from '@shared/helpers/math.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Shape, Storey } from '@shared/models/design/storey.model';
import type { Polygon2D } from '@shared/models/geometry.model';
import { designGuards } from './design-typeguards.helpers';
import { getCutoutShapes, getSolidShapes } from './cutout.helpers';
import {
  ELEVATION_TOUCH_EPSILON,
  getElevationRangeOverlapHeight,
  getStoreyElevationRange,
  type ElevationRange,
} from './storey.helpers';

/** Ignore sub-square-millimetre polygon overlap from floating-point noise. */
const FLOOR_OVERLAP_EPSILON = 1e-6;

type StoreyEnvelope = {
  storey: Storey;
  range: ElevationRange;
  isMezzanine: boolean;
};

/**
 * Per-storey envelope metrics after subtracting party walls and stacking
 * coverage from other buildings on the site.
 */
export type StoreyEnvelopeMetrics = {
  /** Length of walls shared with elevation-overlapping neighbours. */
  sharedWallLength: number;
  /** Shared wall length × overlapping height (m² of party wall). */
  sharedWallArea: number;
  /** True when no other storey's solid footprint sits strictly below this one. */
  isGroundFloor: boolean;
  /** Net floor area of storeys whose top touches this storey's bottom. */
  coveredGfaBelow: number;
  /** Net floor area of storeys whose bottom touches this storey's top. */
  coveredGfaAbove: number;
};

const emptyMetrics = (): StoreyEnvelopeMetrics => ({
  sharedWallLength: 0,
  sharedWallArea: 0,
  isGroundFloor: true,
  coveredGfaBelow: 0,
  coveredGfaAbove: 0,
});

/**
 * Elevation of every storey on the site, from {@link getStoreyElevationRange}
 * so stacking and mezzanines match storey helpers and overlap validation.
 */
const collectStoreyEnvelopes = (design: BuildingDesign): StoreyEnvelope[] =>
  design.buildings.flatMap((building) =>
    building.storeys.map((storey) => ({
      storey,
      range: getStoreyElevationRange(building.storeys, storey),
      isMezzanine: designGuards.is.mezzanineStorey(storey),
    })),
  );

const polygonOf = (shape: Shape): Polygon2D | undefined =>
  isPolygon2D(shape.points) ? shape.points : undefined;

/**
 * Sum a pairwise polygon metric over two shape lists.
 */
const sumPairwisePolygons = (
  a: Shape[],
  b: Shape[],
  measure: (polygonA: Polygon2D, polygonB: Polygon2D) => number,
): number => {
  let total = 0;
  for (const shapeA of a) {
    const polygonA = polygonOf(shapeA);
    if (!polygonA) continue;
    for (const shapeB of b) {
      const polygonB = polygonOf(shapeB);
      if (!polygonB) continue;
      total += measure(polygonA, polygonB);
    }
  }
  return total;
};

/**
 * Footprints denser than this skip exact clipping when deciding ground floor.
 * A project open spent ~20s in Martinez on every storey pair; sub-millimetre
 * noise is still measured exactly for simple outlines.
 */
const DENSE_FOOTPRINT_VERTEX_COUNT = 12;

/**
 * True when solid footprints overlap enough to count as a storey below.
 * Party-wall contact is false. Simple outlines still use exact area so a
 * sub-millimetre sliver does not remove ground-floor status.
 */
const solidFloorsOverlap = (a: Storey, b: Storey): boolean => {
  const solidsA = getSolidShapes(a);
  const solidsB = getSolidShapes(b);
  for (const shapeA of solidsA) {
    const polygonA = polygonOf(shapeA);
    if (!polygonA) continue;
    for (const shapeB of solidsB) {
      const polygonB = polygonOf(shapeB);
      if (!polygonB) continue;
      if (!doPolygonsOverlapInterior(polygonA, polygonB)) continue;
      const dense =
        polygonA.length > DENSE_FOOTPRINT_VERTEX_COUNT ||
        polygonB.length > DENSE_FOOTPRINT_VERTEX_COUNT;
      if (dense) return true;
      if (getPolygonOverlapArea(polygonA, polygonB) > FLOOR_OVERLAP_EPSILON) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Overlap of remaining floor (solids minus cutouts) via inclusion-exclusion.
 * Matching shafts cancel; a slab over a courtyard does not cover the hole.
 */
const netFloorOverlapArea = (a: Storey, b: Storey): number => {
  const solidsA = getSolidShapes(a);
  const solidsB = getSolidShapes(b);
  const cutoutsA = getCutoutShapes(a);
  const cutoutsB = getCutoutShapes(b);
  return Math.max(
    0,
    sumPairwisePolygons(solidsA, solidsB, getPolygonOverlapArea) -
      sumPairwisePolygons(solidsA, cutoutsB, getPolygonOverlapArea) -
      sumPairwisePolygons(cutoutsA, solidsB, getPolygonOverlapArea) +
      sumPairwisePolygons(cutoutsA, cutoutsB, getPolygonOverlapArea),
  );
};

/**
 * Party-wall length between two storeys. Net floor overlap is stacking (or
 * invalid overlap), not a shared wall. Solids against the other storey's
 * cutouts count so a building in a courtyard shares the inner edge.
 */
const sharedWallLengthBetween = (
  a: StoreyEnvelope,
  b: StoreyEnvelope,
): number => {
  if (a.isMezzanine || b.isMezzanine) return 0;
  // Remaining floor overlap is a stack, not a party wall.
  if (netFloorOverlapArea(a.storey, b.storey) > FLOOR_OVERLAP_EPSILON) return 0;

  const solidsA = getSolidShapes(a.storey);
  const solidsB = getSolidShapes(b.storey);
  const cutoutsA = getCutoutShapes(a.storey);
  const cutoutsB = getCutoutShapes(b.storey);
  return (
    sumPairwisePolygons(solidsA, solidsB, getSharedBoundaryLength) +
    sumPairwisePolygons(solidsA, cutoutsB, getSharedBoundaryLength) +
    sumPairwisePolygons(cutoutsA, solidsB, getSharedBoundaryLength)
  );
};

/**
 * Envelope metrics for every storey in {@link design}, keyed by storey id.
 * Party walls are subtracted only where storeys overlap in elevation, including
 * solid-to-cutout edges. Covered GFA is net floor (cutouts removed). Overlapping
 * covering buildings are invalid, so pairwise cover is the union. A storey is
 * not on grade when another solid footprint sits strictly below it.
 */
const computeStoreyEnvelopeMetricsById = (
  design: BuildingDesign,
): Record<Storey['id'], StoreyEnvelopeMetrics> => {
  const envelopes = collectStoreyEnvelopes(design);
  const result: Record<Storey['id'], StoreyEnvelopeMetrics> = {};

  for (const current of envelopes) {
    const metrics = emptyMetrics();
    if (current.isMezzanine) {
      metrics.isGroundFloor = false;
      result[current.storey.id] = metrics;
      continue;
    }

    let hasStoreyBelow = false;

    for (const other of envelopes) {
      if (other.storey.id === current.storey.id) continue;
      if (other.isMezzanine) continue;

      const overlapHeight = getElevationRangeOverlapHeight(
        current.range,
        other.range,
      );
      if (overlapHeight > 0) {
        const wallLength = sharedWallLengthBetween(current, other);
        metrics.sharedWallLength += wallLength;
        // Taller neighbour keeps the unshared extra height as external wall.
        metrics.sharedWallArea += wallLength * overlapHeight;
      }

      // Solid overlap (ignoring holes) still means something sits below.
      // Exact area is not needed; party-wall edges do not count as interior.
      if (
        !hasStoreyBelow &&
        other.range.bottom < current.range.bottom &&
        solidFloorsOverlap(current.storey, other.storey)
      ) {
        hasStoreyBelow = true;
      }

      const otherCoversCurrent = isCloseTo(
        other.range.top,
        current.range.bottom,
        ELEVATION_TOUCH_EPSILON,
      );
      const currentCoversOther = isCloseTo(
        other.range.bottom,
        current.range.top,
        ELEVATION_TOUCH_EPSILON,
      );
      // Non-touching storeys never contribute covered GFA. Skip the clip.
      if (!otherCoversCurrent && !currentCoversOther) continue;

      const floorOverlap = netFloorOverlapArea(current.storey, other.storey);
      if (floorOverlap <= FLOOR_OVERLAP_EPSILON) continue;

      // Touching top-to-bottom counts as a supporting/covering slab, not a gap.
      if (otherCoversCurrent) metrics.coveredGfaBelow += floorOverlap;
      if (currentCoversOther) metrics.coveredGfaAbove += floorOverlap;
    }

    metrics.isGroundFloor = !hasStoreyBelow;
    result[current.storey.id] = metrics;
  }

  return result;
};

/**
 * Envelope metrics for every storey in {@link design}, keyed by storey id.
 * Cached per design identity so activity mix and expression variables share
 * one matrix instead of recomputing per storey lookup.
 */
export const getStoreyEnvelopeMetricsById = (
  design: BuildingDesign,
): Record<Storey['id'], StoreyEnvelopeMetrics> =>
  cacheFactory(
    () => computeStoreyEnvelopeMetricsById(design),
    'getStoreyEnvelopeMetricsById',
    [design],
  );

/**
 * Party-wall length to subtract from a storey's 2D perimeter so
 * `perimeter * height` matches GWA. Equal-height neighbours subtract the
 * full shared length; a taller storey only subtracts the overlapping fraction.
 */
export const getSharedLengthForPerimeter = (
  metrics: Pick<StoreyEnvelopeMetrics, 'sharedWallArea'> | undefined,
  height: number,
): number => {
  if (!metrics || height <= 0) return 0;
  return metrics.sharedWallArea / height;
};
